/**
 * KPI Card Component
 * Displays key performance indicator with icon, value, and trend
 *
 * @description
 * A reusable card component for displaying KPI metrics with:
 * - Icon and title
 * - Primary value display
 * - Optional subtitle
 * - Optional badge
 * - Optional trend indicator with percentage
 * - Loading skeleton state
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-3
 */

import React from 'react';
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface KPICardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  subtitle?: string;
  badge?: {
    text: string;
    variant?: 'default' | 'warning' | 'success' | 'destructive' | 'secondary' | 'outline';
  };
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  loading?: boolean;
  className?: string;
}

// ============================================
// LOADING SKELETON
// ============================================

const KPICardSkeleton: React.FC = () => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-5 rounded" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-20 mb-2" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon: Icon,
  subtitle,
  badge,
  trend,
  loading = false,
  className
}) => {
  // Show skeleton during loading
  if (loading) {
    return <KPICardSkeleton />;
  }

  return (
    <Card className={cn('transition-all hover:shadow-md', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-5 w-5 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline justify-between">
          <div className="text-2xl font-bold">{value}</div>
          {trend && (
            <div
              className={cn(
                'flex items-center text-xs font-medium',
                trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
              )}
            >
              {trend.direction === 'up' ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {Math.abs(trend.value)}%
            </div>
          )}
        </div>

        {(subtitle || badge) && (
          <div className="flex items-center justify-between mt-2">
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {badge && (
              <Badge variant={badge.variant || 'default'} className="ml-auto">
                {badge.text}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
